import { Component, ElementRef, Input, OnDestroy, OnInit } from "@angular/core";
import { Connection } from "@common/ADAPT.Common.Model/organisation/connection";
import { Role } from "@common/ADAPT.Common.Model/organisation/role";
import { AssignableRoleTypes, RoleTypeCode } from "@common/ADAPT.Common.Model/organisation/role-type-code";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { MethodologyPredicate } from "@common/lib/data/methodology-predicate";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { CommonIntegratedArchitectureFrameworkService } from "@org-common/lib/architecture/common-integrated-architecture-framework.service";
import { CommonIntegratedArchitectureFrameworkAuthService } from "@org-common/lib/architecture/common-integrated-architecture-framework-auth.service";
import { IntegratedArchitectureFrameworkQueryUtilities } from "@org-common/lib/architecture/integrated-architecture-framework-query-utilities";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { AuthorisationNotificationService } from "@org-common/lib/authorisation/authorisation-notification.service";
import { DirectorySharedService } from "@org-common/lib/directory-shared/directory-shared.service";
import { EditTeamRolesDialogComponent } from "@org-common/lib/team-roles/edit-team-roles-dialog/edit-team-roles-dialog.component";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { CommonTeamsAuthService } from "@org-common/lib/teams/common-teams-auth.service";
import { lastValueFrom, switchMap } from "rxjs";
import { catchError, finalize } from "rxjs/operators";
import { Options as SortablejsOptions, SortableEvent } from "sortablejs";

@Component({
    selector: "adapt-team-roles",
    templateUrl: "./team-roles.component.html",
    styleUrl: "./team-roles.component.scss",
    standalone: false,
})
export class TeamRolesComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input({ required: true }) public team!: Team;
    @Input() public nested = false;
    @Input() public isEditing = false;

    public canEdit = false;
    public groupByPerson = false;
    public hidePeople = false;
    private firstUpdate = true;

    public roles: Role[] = [];
    public visibleRoles: Role[] = [];
    public activeConnections = new Map<number, Connection>();
    public activeConnectionsForRole = new Map<Role, Set<Connection>>();
    public rolesForActiveConnections = new Map<Connection, Set<Role>>();

    public personDragging?: Connection;

    private sharedSortableJsOptions: SortablejsOptions = {
        // Mandatory for a good performance when using lots of shared lists.
        animation: 0,
        forceFallback: true,

        scroll: true,
        bubbleScroll: true,
        scrollSensitivity: 100,
        scrollSpeed: 50,

        sort: false,
    };

    public peopleListOptions: SortablejsOptions = {
        ...this.sharedSortableJsOptions,
        group: {
            name: "people-list",
            pull: "clone",
            put: false,
        },
        handle: ".card-person",
        revertOnSpill: true,
        onStart: (e) => {
            if (e.item) {
                const { connection } = this.getDraggedConnectionToRole(e.item);
                this.personDragging = connection;
            }
        },
        onSpill: () => this.personDragging = undefined,
        onEnd: this.createRoleConnection,
    };

    public roleListOptions: SortablejsOptions = {
        ...this.sharedSortableJsOptions,
        group: {
            name: "people-list",
            pull: false,
            put: (to, _, dragEl, __) => {
                const { connection, role } = this.getDraggedConnectionToRole(dragEl, to.el);
                if (!connection || !role || role.extensions.isSystemAllocatedRole()) {
                    return false;
                }

                // cannot drop connection into group with that connection already
                const roleConnections = this.activeConnectionsForRole.get(role);
                return !roleConnections?.has(connection);
            },
        },
        filter: () => true,
        preventOnFilter: true,
    };

    private readonly archData = new IntegratedArchitectureFrameworkQueryUtilities(this.commonDataService);
    private readonly sortConnectionsByFullNameFunction = SortUtilities.getSortByFieldFunction<Connection>("person.fullName");

    public constructor(
        protected elementRef: ElementRef,
        private commonDataService: CommonDataService,
        private commonDialogService: AdaptCommonDialogService,
        private commonTeamsService: CommonTeamsService,
        private commonTeamsAuthService: CommonTeamsAuthService,
        private authorisationService: AuthorisationService,
        private directorySharedService: DirectorySharedService,
        private commonIntegratedArchitectureService: CommonIntegratedArchitectureFrameworkService,
        authorisationNotificationService: AuthorisationNotificationService,
    ) {
        super(elementRef);

        authorisationNotificationService.authorisationChanged$.pipe(
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    public async ngOnInit() {
        await this.updateData();
    }

    @Autobind
    public edit() {
        return this.commonDialogService.open(EditTeamRolesDialogComponent, this.team).pipe(
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        );
    }

    @Autobind
    public togglePinnedRole(role: Role) {
        return this.commonIntegratedArchitectureService.getRoleLocationsForTeam(this.team.teamId).pipe(
            switchMap((roleLocations) => {
                const roleLocation = roleLocations.find((rl) => rl.roleId === role.roleId);
                return !roleLocation
                    ? this.commonIntegratedArchitectureService.createRoleLocationForTeam(role, this.team!)
                    : this.commonDataService.remove(roleLocation);
            }),
            switchMap((roleLocation) => this.saveEntitiesAndHandleSaveFailure([roleLocation!])),
        );
    }

    public async onRolesAdded(roles: Role[]) {
        await this.updateData();

        // highlight the newly added roles
        setTimeout(() => {
            for (const role of roles) {
                const element = this.elementRef.nativeElement?.querySelector(`[data-role-id='${role.roleId}']`);
                if (element) {
                    element.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                    element.classList.add("highlight");
                    setTimeout(() => element.classList.remove("highlight"), 2500);
                }
            }
        });
    }

    public async updateData() {
        this.personDragging = undefined;

        const hasTier2Edit = await this.authorisationService.promiseToGetHasAccess(CommonIntegratedArchitectureFrameworkAuthService.EditAnyTier2);
        const hasManageAccess = await this.directorySharedService.promiseToVerifyAccessToManageAccess();
        const hasTeamConfigure = this.commonTeamsAuthService.currentPersonCanEditTeam(this.team);
        this.canEdit = hasTeamConfigure || (this.commonTeamsAuthService.currentPersonCanReadTeam(this.team) && (hasTier2Edit || hasManageAccess));

        // pinned team roles should always be visible
        // need to fetch this first so we can get the right roles in the following fetches
        const teamRoleLocations = await lastValueFrom(this.commonIntegratedArchitectureService.getRoleLocationsForTeam(this.team.teamId));
        const teamRoleIds = teamRoleLocations.map((rl) => rl.roleId);

        // fetch roles and populate visible roles
        const visibleRoleTypeCodes = [...AssignableRoleTypes, RoleTypeCode.CulturalLeader, RoleTypeCode.Tier1];
        const rolePredicate = new MethodologyPredicate<Role>("roleType.code", "in", visibleRoleTypeCodes)
            .or(new MethodologyPredicate<Role>("roleId", "in", teamRoleIds));
        this.roles = (await this.directorySharedService.promiseToGetActiveRolesByPredicate(rolePredicate))
            .sort(SortUtilities.getSortByFieldFunction<Role>("label"));
        this.visibleRoles = this.roles.filter((role) => teamRoleIds.includes(role.roleId));

        if (this.firstUpdate && !this.isEditing) {
            this.hidePeople = this.visibleRoles.length > 4;
            this.firstUpdate = false;
        }

        const teamRoleConnections = await this.commonTeamsService.promiseToGetTeamMemberRoleConnections(this.team, true);
        this.activeConnections = new Map(teamRoleConnections
            .map((rc) => rc.connection)
            .sort(this.sortConnectionsByFullNameFunction)
            .map((c) => [c.connectionId, c]));

        this.activeConnectionsForRole.clear();
        for (const role of this.roles) {
            const roleConnections = await lastValueFrom(this.archData.getRoleConnectionsForRoleId(role.roleId, true));
            const activeConnectionsForRole = roleConnections
                .map((rc) => rc.connection)
                .filter((c) => this.activeConnections.has(c.connectionId))
                .sort(this.sortConnectionsByFullNameFunction);
            this.activeConnectionsForRole.set(role, new Set(activeConnectionsForRole));
        }

        this.rolesForActiveConnections.clear();
        for (const connection of this.activeConnections.values()) {
            const roleConnections = await lastValueFrom(this.archData.getRoleConnectionsForPersonId(connection.personId, true));
            const rolesForActiveConnection = roleConnections
                .map((rc) => rc.role)
                .filter((role) => this.visibleRoles.includes(role))
                .sort(SortUtilities.getSortByFieldFunction<Role>("label"));
            this.rolesForActiveConnections.set(connection, new Set(rolesForActiveConnection));
        }
    }

    @Autobind
    private async createRoleConnection(e: SortableEvent) {
        this.personDragging = undefined;

        const { connection, role } = this.getDraggedConnectionToRole(e.item, e.to);
        if (!connection || !role) {
            return;
        }

        const roleConnection = await lastValueFrom(this.archData.createRoleConnection({ connection, role }));

        this.saveEntitiesAndHandleSaveFailure([roleConnection], () => {
            // remove the dragged clone after the update otherwise it'll just stay there
            e.item?.remove();
        }).subscribe();
    }

    private getDraggedConnectionToRole(draggedElement: HTMLElement, targetElement?: HTMLElement) {
        const dragConnectionId = Number(draggedElement.dataset.connectionId);
        const connection = this.activeConnections.get(dragConnectionId);

        const dropRoleId = targetElement
            ? Number(targetElement.dataset.roleId)
            : undefined;
        const role = dropRoleId
            ? this.roles.find((r) => r.roleId === dropRoleId)
            : undefined;

        return { connection, role };
    }

    private saveEntitiesAndHandleSaveFailure(entities: IBreezeEntity[], finalizeCallback?: () => void) {
        return this.commonDataService.saveEntities(entities).pipe(
            catchError(() => this.commonDialogService.handleSaveFailure(entities)),
            // cannot use switchMap here as cancelling the save fail dialog won't continue the observable
            finalize(() => {
                finalizeCallback?.();
                return this.updateData();
            }),
            this.takeUntilDestroyed(),
        );
    }
}
